import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  DataRoomActivityWindow,
  DEFAULT_DATA_ROOM_ACTIVITY_WINDOW,
  formatViewingTime,
  VisitorActivityRow,
  VisitorSortColumn,
} from 'shared';
import { twJoin } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { Search } from '../../../../components/search/index';
import { TableRow } from '../../../../components/table/index';
import { StringKey } from '../../../../lang/index';
import { Order } from '../../../../types/general.types';
import { PageRoute } from '../../../../types/page.types';
import { AccessStatusBadge } from '../../workspaces/components/access/access-badges';
import { LastActive } from '../../workspaces/components/last-active';
import { ActivityEmptyState } from '../components/activity-empty-state';
import { ActivityLoadError } from '../components/activity-load-error';
import { ActivityTablePlaceholder } from '../components/activity-loading-placeholders';
import { ActivityPagedTable } from '../components/activity-paged-table';
import { ActivityWindowToggle } from '../components/activity-window-toggle';
import { FilterSelect } from '../components/filter-select';
import { ariaSortOf, SortableTitle } from '../components/sortable-title';
import { VisitorName } from '../components/visitor-name';
import { VisitorTypeLabel } from '../components/visitor-type-label';
import { useActivityVisitors } from '../hooks/use-activity-visitors.hook';
import { ActivityOutletContext } from '../types/activity-outlet-context.types';

enum Column {
  ACCESS = 'access',
  DATA_ROOM = 'dataRoom',
  DOWNLOADS = 'downloads',
  LAST_ACTIVE = 'lastActive',
  OPEN = 'open',
  TIME_SPENT = 'timeSpent',
  TYPE = 'type',
  VIEWS = 'views',
  VISITOR = 'visitor',
}

type TitledColumn = Exclude<Column, Column.OPEN>;

/**
 * 20.1.4 §2, as the design lays it out: the organisation rides under the visitor's name and the
 * arrow opens their profile.
 *
 * Type and Access sit at the end, as they do in a room's own Visitor Activity panel. A row is the
 * visitor's activity and the grant is a different fact about them, but an Admin reading this table
 * is asking who these people are and whether they can still get in — and the answer was two
 * clicks away on a screen that already had the room in the row.
 */
const COLUMN_ORDER = [
  Column.VISITOR,
  Column.DATA_ROOM,
  Column.LAST_ACTIVE,
  Column.VIEWS,
  Column.TIME_SPENT,
  Column.DOWNLOADS,
  Column.TYPE,
  Column.ACCESS,
  Column.OPEN,
];

const COLUMN_TITLE: Record<TitledColumn, StringKey> = {
  [Column.ACCESS]: 'ACCESS',
  [Column.DATA_ROOM]: 'DATA_ROOM',
  [Column.DOWNLOADS]: 'DOWNLOADS',
  [Column.LAST_ACTIVE]: 'LAST_ACTIVE',
  [Column.TIME_SPENT]: 'TIME_SPENT',
  [Column.TYPE]: 'TYPE',
  [Column.VIEWS]: 'VIEWS',
  [Column.VISITOR]: 'VISITOR',
};

/** 20.1.4 §3: only these re-sort, which is also the server's allowlist. */
const SORTABLE: Partial<Record<Column, VisitorSortColumn>> = {
  [Column.DATA_ROOM]: VisitorSortColumn.DATA_ROOM,
  [Column.DOWNLOADS]: VisitorSortColumn.DOWNLOADS,
  [Column.LAST_ACTIVE]: VisitorSortColumn.LAST_ACTIVE,
  [Column.TIME_SPENT]: VisitorSortColumn.TIME_SPENT,
  [Column.VIEWS]: VisitorSortColumn.VIEWS,
  [Column.VISITOR]: VisitorSortColumn.VISITOR,
};

/**
 * §3 reads the two text columns as A–Z and the three numeric ones as "most first", so a column's
 * first click asks for the direction that column is useful in rather than always descending.
 */
const FIRST_ORDER: Record<VisitorSortColumn, Order> = {
  [VisitorSortColumn.DATA_ROOM]: Order.ASC,
  [VisitorSortColumn.DOWNLOADS]: Order.DESC,
  [VisitorSortColumn.LAST_ACTIVE]: Order.DESC,
  [VisitorSortColumn.TIME_SPENT]: Order.DESC,
  [VisitorSortColumn.VIEWS]: Order.DESC,
  [VisitorSortColumn.VISITOR]: Order.ASC,
};

// The organisation, derived from the address — the one detail under the name on this screen.
const visitorDetails = (row: VisitorActivityRow): string[] =>
  [row.organisation].filter((detail): detail is string => Boolean(detail));

/**
 * The Visitors tab — 20.1.4 §1 to §4.
 *
 * A row is one visitor in one data room (§1), so the same person invited to three rooms is three
 * rows here — the opposite of the Most Active Visitor card on Overview, which sums a person across
 * their grants. Live rooms only (20.1.0 §11): a deleted room keeps its share of the company totals
 * and leaves every list, because there is nothing left to open.
 *
 * The window is this tab's own, as every tab's is; search is on the name, because an Admin looking
 * for somebody types a person and not an address.
 */
export const ActivityVisitorsTab: FC = () => {
  const navigate = useNavigate();
  const { toolbar } = useOutletContext<ActivityOutletContext>();
  const [window, setWindow] = useState<DataRoomActivityWindow>(DEFAULT_DATA_ROOM_ACTIVITY_WINDOW);
  const [search, setSearch] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [sortBy, setSortBy] = useState<VisitorSortColumn>(VisitorSortColumn.LAST_ACTIVE);
  const [sortOrder, setSortOrder] = useState<Order>(Order.DESC);

  // Every filter and order is a query of its own, so each starts from its first page again.
  const { count, isError, isPending, organisations, pagination, retry, rows } = useActivityVisitors(
    {
      organisation: organisation || undefined,
      search: search.trim() || undefined,
      sortBy,
      sortOrder,
      window,
    },
  );

  const reSort = (column: Column): void => {
    const next = SORTABLE[column];

    if (!next) return;

    setSortOrder(
      next === sortBy ? (sortOrder === Order.DESC ? Order.ASC : Order.DESC) : FIRST_ORDER[next],
    );
    setSortBy(next);
  };

  // The list is gone while the next one loads; the chosen organisation stays listed so the
  // button keeps its name instead of flashing back to "All Organizations". Added last, so the
  // options keep the server's order whenever it is there.
  const organisationOptions = (
    organisation && !organisations.includes(organisation)
      ? [...organisations, organisation]
      : organisations
  ).map((name) => ({ label: name, value: name }));

  const toolbarControls = (
    <div className="flex items-center gap-3">
      <Search buttonClassName="bg-white shadow-xs" callback={setSearch} />
      {/* §4: the organisations come with the page, so the list always matches what can be found. */}
      <FilterSelect
        allLabel="ALL_ORGANIZATIONS"
        onChange={setOrganisation}
        options={organisationOptions}
        value={organisation}
      />
      <ActivityWindowToggle onChange={setWindow} value={window} />
    </div>
  );

  const renderContent = () => {
    if (isPending) {
      return <ActivityTablePlaceholder columns={6} />;
    }

    if (isError) {
      return <ActivityLoadError onRetry={() => void retry()} />;
    }

    // Nobody has visited anything yet, and nothing matching this filter, say different things.
    if (count === 0) {
      return (
        <div className="flex h-full min-h-0 w-full flex-1 flex-col items-center">
          {search.trim() || organisation ? (
            <span className="pt-[72px] text-sm font-[450] text-gray-400">
              <AppFormattedMessage id="NO_VISITORS_MATCH_SEARCH" />
            </span>
          ) : (
            <ActivityEmptyState
              description="NO_VISITOR_ACTIVITY_HINT"
              title="NO_VISITOR_ACTIVITY"
            />
          )}
        </div>
      );
    }

    return (
      // Fades in on arrival; a new window, filter or order dims it while its first page loads.
      <div
        className={twJoin(
          'flex h-full min-h-0 w-full flex-1 animate-reveal-fade flex-col gap-4 overflow-y-auto p-4 transition-opacity duration-200',
          pagination.isReloading && 'opacity-60',
        )}
      >
        <ActivityPagedTable
          ariaSort={ariaSortOf(SORTABLE, sortBy, sortOrder)}
          // Two columns wider than it was drawn, so neither Type nor Access squeezes the rest.
          className="min-w-[1240px]"
          columnOrder={COLUMN_ORDER}
          columnsTitle={{
            ...Object.fromEntries(
              Object.entries(COLUMN_TITLE).map(([column, title]) => [
                column,
                <SortableTitle
                  key={column}
                  onSort={SORTABLE[column as Column] && (() => reSort(column as Column))}
                  title={title}
                />,
              ]),
            ),
            [Column.OPEN]: null,
          }}
          pagination={pagination}
          renderRow={(row, className) => (
            <TableRow
              columnOrder={COLUMN_ORDER}
              contentClassName={{ general: 'gap-2 px-4', [Column.OPEN]: 'justify-center px-2' }}
              fields={{
                // One visitor in one room, so neither half identifies the row on its own.
                id: `${row.visitorKey}-${row.dataRoomId}`,
                [Column.VISITOR]: <VisitorName details={visitorDetails(row)} name={row.name} />,
                [Column.DATA_ROOM]: (
                  <span className="block truncate" title={row.dataRoomName}>
                    {row.dataRoomName}
                  </span>
                ),
                [Column.LAST_ACTIVE]: <LastActive at={row.lastActiveAt} />,
                [Column.VIEWS]: row.views,
                [Column.TIME_SPENT]: formatViewingTime(row.viewingTimeMs),
                [Column.DOWNLOADS]: row.downloads,
                [Column.TYPE]: <VisitorTypeLabel actorType={row.actorType} />,
                [Column.ACCESS]: row.accessStatus ? (
                  <AccessStatusBadge status={row.accessStatus} />
                ) : (
                  <span className="text-gray-400">–</span>
                ),
                [Column.OPEN]: <ChevronDownIcon className="-rotate-90" iconColor="#475467" />,
              }}
              key={`${row.visitorKey}-${row.dataRoomId}`}
              labelClassName="min-w-0 text-sm"
              onClick={() =>
                navigate(
                  PageRoute.DATA_ROOM_ACTIVITY_VISITOR.replace(':visitorKey', row.visitorKey),
                )
              }
              tdClassName="p-0"
              trClassName={twJoin(
                'cursor-pointer transition-colors duration-150 hover:bg-gray-50',
                className,
              )}
            />
          )}
          rows={rows}
          tHeadClassNames={{
            thClassName: {
              // The visitor takes what is left, and never less than the name and its details need
              // to sit clear of the room beside them.
              general: 'px-4',
              [Column.DATA_ROOM]: 'w-[200px]',
              [Column.DOWNLOADS]: 'w-28',
              [Column.LAST_ACTIVE]: 'w-[150px]',
              [Column.OPEN]: 'w-[54px]',
              [Column.TIME_SPENT]: 'w-[140px]',
              [Column.VIEWS]: 'w-24',
            },
          }}
        />
      </div>
    );
  };

  return (
    <>
      {/* Drawn beside the sub-tab buttons, but every filter here stays this tab's own. */}
      {toolbar && createPortal(toolbarControls, toolbar)}
      {renderContent()}
    </>
  );
};
